import type { QueryClient } from '@tanstack/react-query';
import { KEY_TOKEN, clearAuth, setAuth, type StoredUser } from '@/lib/auth-storage';

/**
 * Início e fim de sessão num lugar só.
 *
 * O cache de consultas guarda o que a pessoa viu (perfil, processos, chaves de acesso,
 * usuários...) e sobrevive à navegação dentro do app. Se não for descartado na troca de
 * sessão, quem entra na mesma aba vê por alguns minutos os dados de quem saiu - e o perfil
 * velho decide as rotas (ex.: primeiro acesso preso no passo da senha que já foi trocada).
 */

/** Guarda a credencial de quem acabou de entrar, descartando os dados da sessão anterior. */
export function iniciarSessao(queryClient: QueryClient, token: string, user: StoredUser): void {
  queryClient.clear();
  setAuth(token, user);
}

/**
 * Encerra a sessão: apaga a credencial e recarrega o app em /login. Recarregar descarta toda
 * a memória da aba (cache, consultas em andamento, estado das telas), não só o cache.
 */
export function encerrarSessao(): void {
  clearAuth();
  window.location.replace('/login');
}

/** Quem é o dono do token (claim `sub` do JWT), ou null se não houver token legível. */
export function donoDoToken(token: string | null): string | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const { sub } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      sub?: unknown;
    };
    return sub == null ? null : String(sub);
  } catch {
    return null;
  }
}

/**
 * Outra aba saiu ou entrou com outra pessoa: recarrega esta aba para não misturar os dados em
 * memória de uma pessoa com a credencial de outra. Um novo login da MESMA pessoa não recarrega
 * (não perde o que estiver sendo preenchido aqui).
 *
 * @returns função que para de observar
 */
export function observarOutrasAbas(): () => void {
  const aoMudar = (e: StorageEvent) => {
    if (e.key !== KEY_TOKEN) return;
    if (donoDoToken(e.oldValue) !== donoDoToken(e.newValue)) {
      window.location.reload();
    }
  };
  window.addEventListener('storage', aoMudar);
  return () => window.removeEventListener('storage', aoMudar);
}
